import { config } from '../config/environment';
import { 
  dummyServers, 
  dummyLocations, 
  dummyShifts, 
  dummyInteractions, 
  dummyServerStats,
  dummyTrialData,
  getDummyDataForQuery,
  createDummyApiResponse
} from '../data/dummyData';

// Service to handle dummy data responses in dev mode
export class DummyDataService {
  private static isDevMode(): boolean {
    return config.useDummyData;
  }

  // Simulate API call with dummy data
  public static async simulateApiCall(path: string, options: RequestInit = {}): Promise<Response> {
    if (!this.isDevMode()) {
      throw new Error('DummyDataService should only be used in dev mode');
    }

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));

    let responseData: any = null;
    let status = 200;

    try {
      if (path.includes('/admin/execute-select')) {
        // Extract SQL from query parameters
        const url = new URL(`http://localhost${path}`);
        const sql = url.searchParams.get('sql');
        
        if (sql) {
          responseData = createDummyApiResponse(getDummyDataForQuery(sql));
        } else {
          responseData = createDummyApiResponse([]);
        }
      } else if (path.includes('/challenges')) {
        // /challenges/mine and /locations/{id}/challenges both pass through here.
        // ?probe=unknown appends an unknown-criterion to exercise the placeholder
        // without needing the backend. ?probe=newer flips schema_version > 1.
        const probeUrl = new URL(`http://localhost${path}`);
        const probe = probeUrl.searchParams.get('probe');
        const criteria: any[] = [
          {
            type: 'simple_progress',
            label: 'Team XP',
            current: 6200,
            target: 10000,
            unit: null,
          },
          {
            type: 'trend_metric',
            label: 'Items per transaction',
            timeframe: 'last 14 days',
            baseline: 2.0,
            current: 2.18,
            target: 2.3,
            delta_label: '+9% since start',
            trend: [2.0, 2.02, 2.05, 2.07, 2.1, 2.12, 2.14, 2.15, 2.16, 2.17, 2.18],
          },
        ];
        if (probe === 'unknown') {
          criteria.push({ type: 'future_type_xyz', label: 'Mystery metric' });
        }
        responseData = {
          challenges: [
            {
              id: '11111111-1111-1111-1111-111111111111',
              location_id: '33333333-3333-3333-3333-333333333333',
              is_active: true,
              status: {
                schema_version: probe === 'newer' ? 2 : 1,
                title: 'Team Dinner Challenge',
                subtitle: "Earn a team meal at PF Chang's",
                icon: 'team_dinner',
                description:
                  'Every module and upsell your team does adds up. Hit both goals to win.',
                criteria,
              },
            },
          ],
        };
      } else if (path.includes('/servers')) {
        responseData = dummyServers;
      } else if (path.includes('/locations')) {
        responseData = dummyLocations;
      } else if (path.includes('/shifts')) {
        responseData = dummyShifts;
      } else if (path.includes('/interactions')) {
        responseData = dummyInteractions;
      } else if (path.includes('/server-stats')) {
        responseData = dummyServerStats;
      } else if (path.includes('/trial-analysis')) {
        responseData = dummyTrialData;
      } else {
        // Default response for unknown endpoints
        responseData = { message: 'Dummy data response', path };
      }

      // Create a mock Response object
      const mockResponse = new Response(JSON.stringify(responseData), {
        status,
        statusText: 'OK',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return mockResponse;
    } catch (error) {
      // Simulate error response
      const errorResponse = new Response(JSON.stringify({ 
        error: 'Dummy data error', 
        message: error instanceof Error ? error.message : 'Unknown error' 
      }), {
        status: 500,
        statusText: 'Internal Server Error',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return errorResponse;
    }
  }

  // Helper method to check if we should use dummy data
  public static shouldUseDummyData(): boolean {
    return this.isDevMode();
  }
}
